/**
 * Rotas do NÚCLEO — as que qualquer cliente tem, compre o que comprar.
 *
 * PORQUE ESTE FICHEIRO EXISTE
 * ---------------------------
 * A ficha de colaborador, a lista de pessoas e os departamentos viviam em
 * `hr.routes.tsx`. Um cliente sem o módulo de Recursos Humanos ficava sem
 * ficha de colaborador e sem gestão de acessos — a aplicação era inutilizável.
 *
 * Não é hipótese: a EPEL não compra RH, e a API já lhe serve `/users`,
 * `/departments` e `/job-functions` (ver `CORE_ENTITIES_IN_HR` no backend).
 * Era só o frontend que não tinha por onde as mostrar.
 *
 * O que é núcleo e o que é RH
 * ---------------------------
 * NÚCLEO   identidade e acesso — quem é a pessoa, em que departamento está,
 *          que função tem, o que pode ver na aplicação.
 * RH       o que se faz com essa pessoa enquanto empregada — assiduidade,
 *          formações, vencimento, medicina no trabalho, carreira.
 *
 * A ficha reflecte isto nos separadores: os de identidade abrem sempre; os de
 * RH aparecem quando o módulo está ligado. Ver `docs/ficha-do-colaborador-nucleo-e-rh.md`
 * para o desenho completo (a ficha passa a ser um contentor onde cada módulo
 * regista os seus separadores) — isto é o primeiro passo desse plano.
 *
 * As rotas antigas em `/dashboard/hr/employees` continuam a funcionar: quem
 * tem RH não nota diferença, e nenhum link guardado parte.
 */

import { lazy } from "react";

import type { RouteConfig } from "../config";
import { ROUTES } from "../paths";

const UserDetailPage = lazy(() =>
  import("@/pages/users/detail-page").then((m) => ({ default: m.UserDetailPage })),
);

const ColaboradoresPage = lazy(() =>
  import("@/pages/hr/employees-page").then((m) => ({ default: m.ColaboradoresPage })),
);

// O calendário é de cada utilizador e mostra o que cada módulo (com o acesso que
// o utilizador tem) lhe acrescenta. A rota está no núcleo, mas o guarda exige
// `anyModule` (RH, Segurança ou Receção — ver `menu-sections.ts`): sem nenhum deles fecha.
const CalendarPage = lazy(() =>
  import("@/pages/calendar/calendar-page").then((m) => ({ default: m.CalendarPage })),
);

// O criador de processos: núcleo, todo o cliente o tem. Cada pessoa vê os seus, os públicos e aqueles a que foi convidada.
const ProcessMapsPage = lazy(() =>
  import("@/pages/traceability/process-maps-page").then((m) => ({ default: m.ProcessMapsPage })),
);
const ProcessMapEditorPage = lazy(() =>
  import("@/pages/traceability/process-map-editor-page").then((m) => ({ default: m.ProcessMapEditorPage })),
);

const PROCESS_MAPS_READ = { access: "permission" as const, entity: "process_maps", action: "READ" };

/** Ler pessoas: quem gere acessos precisa disto, tenha RH ou não. */
const USER_READ = { access: "permission" as const, entity: "users", action: "READ" };

export const coreRoutes: RouteConfig[] = [
  { path: ROUTES.USERS.LIST, component: ColaboradoresPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.CALENDAR, component: CalendarPage, layout: "dashboard", permissions: { access: "authenticated" } },
  { path: ROUTES.USERS.DETAIL, component: UserDetailPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.PROCESSES.LIST, component: ProcessMapsPage, layout: "dashboard", permissions: PROCESS_MAPS_READ },
  { path: ROUTES.PROCESSES.DETAIL, component: ProcessMapEditorPage, layout: "dashboard", permissions: PROCESS_MAPS_READ },
];
